/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
// import ProductCarouselComponent from "@eisinc/App/Components/ProductCarouselComponent/ProductCarouselComponent";
import XngCarousel, { SwiperStyle } from "@eisinc/App/Components/XngCarousel";
import { sharedCarouselStyles } from "@eisinc/App/SharedStyles/ProductCarouselStyles";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@eisinc/Overrides/Components/ProductCarouselProduct";
import { CarouselCardType } from "@eisinc/XDL/types";
import { hideElementOnPrint } from "@eisinc/XDL/Utilities";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav from "@insite/content-library/Components/SkipNav";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    dropDown = "dropDown",
    erpProductNumbers = "erpProductNumbers",
    brandsSelected = "brandsSelected",
    selectedCategoryIds = "selectedCategoryIds",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    view = "view",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    showAvailability = "showAvailability",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.erpProductNumbers]: string;
        [fields.dropDown]: string;
        [fields.brandsSelected]: string[];
        [fields.selectedCategoryIds]: string[];
        [fields.numberOfProductsToDisplay]: string;
        [fields.view]: CarouselCardType;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.showAvailability]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        randomize: false,
        pageSize: 100,
        select: "id,detailPagePath,logoSmallImagePath,logoAltText",
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
        categoriesDataView: getCategoriesDataView(state),
        products: getProductsForProductInfoList(state, props.id),
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        pageType: getCurrentPage(state).type,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
    loadCategories,
    loadCarouselProducts,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface CuratedProductCarouselStyles {
    carouselStyles?: SwiperStyle;
    carouselProductCondensedStyles?: ProductCarouselProductStyles;
    miniCardStyles?: ProductCarouselProductStyles;
    carouselProductStyles?: ProductCarouselProductStyles;
}

export const styles: CuratedProductCarouselStyles = {
    carouselProductCondensedStyles: {
        ...sharedCarouselStyles.carouselProductCondensedStyles,
    },
    miniCardStyles: {
        ...sharedCarouselStyles.miniCardStyles,
    },
    carouselProductStyles: {
        ...sharedCarouselStyles.carouselProductStyles,
    },
};

const CuratedProductCarousel: React.FC<Props> = props => {
    const { fields, products, id, isTabletScreen, pageType } = props;
    const dispatch = useAppDispatch();
    const afterCarousel = React.createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";

    useEffect(() => {
        const { loadBrandGallery, shouldLoadBrandGallery, brandGalleryParameter, categoriesDataView, loadCategories } =
            props;

        if (fields.dropDown === "brand" && shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }

        if (fields.dropDown === "category" && !categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    }, [fields.dropDown]);

    useEffect(() => {
        if (fields.erpProductNumbers?.trim()) {
            const rawProductNumbers = fields.erpProductNumbers.split(",");
            const productNumbers = [];

            for (let i = 0; i < rawProductNumbers.length; i++) {
                const productNumber = rawProductNumbers[i];

                if (productNumber && productNumber.trim()) {
                    productNumbers.push(productNumber.trim());
                }
            }

            dispatch(
                loadProductInfoList({
                    getProductCollectionParameter: { productNumbers } as any,
                    id,
                } as any),
            );
        }
    }, [fields.erpProductNumbers]);

    useEffect(() => {
        if (
            (fields.dropDown === "category" && fields.selectedCategoryIds && fields.selectedCategoryIds.length) ||
            (fields.dropDown === "brand" && fields.brandsSelected && fields.brandsSelected.length)
        ) {
            const { loadCarouselProducts, id } = props;
            const loadCarouselProductsParams = {
                carouselId: id,
                carouselType: "topSellers",
                numberOfProductsToDisplay: fields.numberOfProductsToDisplay || 10,
            };

            if (fields.dropDown === "category") {
                (loadCarouselProductsParams as any)["selectedCategoryIds"] = fields.selectedCategoryIds;
                (loadCarouselProductsParams as any)["displayProductsFrom"] = "selectedCategories";
            }

            if (fields.dropDown === "brand") {
                (loadCarouselProductsParams as any)["brandIdsSelected"] = fields.brandsSelected;
            }

            loadCarouselProducts(loadCarouselProductsParams as any);
        }
    }, [fields.selectedCategoryIds, fields.numberOfProductsToDisplay, fields.brandsSelected]);

    const carouselCardStyles =
        fields.view === "condensed"
            ? styles.carouselProductCondensedStyles
            : fields.view === "miniCard" || (isTabletScreen && fields.view === "grid")
              ? styles.miniCardStyles
              : styles.carouselProductStyles;

    if (!products || !products.length) {
        return null;
    }

    return (
        <>
            <StyledWrapper
                css={css`
                    ${isProductDetailsPage && hideElementOnPrint}
                `}
            >
                <SkipNav text={translate("Skip Carousel")} destination={afterCarousel} />
                <XngCarousel
                    id={id}
                    navigation={true}
                    spaceBetween={fields.view === "condensed" ? 22 : 24}
                    scrollbar={true}
                    shadowWidth="64px"
                    showArrowsOnMobile={false}
                    extendedStyles={styles.carouselStyles}
                >
                    {products.map((product, index) => (
                        <React.Fragment key={product.id}>
                            <ProductCarouselProduct
                                carouselId={id}
                                product={product}
                                showImage={fields.showImage}
                                showBrand={fields.showBrandName && fields.view !== "condensed"}
                                showTitle={fields.showTitle}
                                showPartNumbers={fields.showPartNumbers && fields.view !== "condensed"}
                                showPrice={fields.showPrice}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={fields.showAddToList && fields.view !== "condensed"}
                                showImageBadges={fields.showImageBadges}
                                maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                showTextBadges={fields.showTextBadges}
                                maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                view={isTabletScreen && fields.view === "grid" ? "miniCard" : fields.view}
                                showAvailability={fields.showAvailability}
                                extendedStyles={carouselCardStyles}
                            />
                        </React.Fragment>
                    ))}
                </XngCarousel>
            </StyledWrapper>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [
        {
            name: fields.dropDown,
            displayName: "Select",
            editorTemplate: "DropDownField",
            options: [
                { value: "manualSelection", displayName: "Manual Selection" },
                { value: "brand", displayName: "Brand" },
                { value: "category", displayName: "Category" },
            ],
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.erpProductNumbers,
            displayName: "ERP Product Numbers",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "e.g 123,321,312",
            isRequired: true,
            isVisible: (item: any) => item.fields.dropDown === "manualSelection",
        },
        {
            name: fields.numberOfProductsToDisplay,
            displayName: "Number Of Products To Display",
            editorTemplate: "IntegerField",
            min: 1,
            max: 20,
            defaultValue: 10,
            fieldType: "General",
            isVisible: (item: any) => item.fields.dropDown === "category" || item.fields.dropDown === "brand",
        },
        {
            name: fields.brandsSelected,
            displayName: "Select brands",
            editorTemplate: "CustomSelectBrandsField",
            defaultValue: [],
            fieldType: "General",
            isVisible: (item: any) => item.fields.dropDown === "brand",
        },
        {
            name: fields.selectedCategoryIds,
            displayName: "Selected Categories",
            editorTemplate: "CategoriesField",
            defaultValue: [],
            fieldType: "General",
            isVisible: (item: any) => item.fields.dropDown === "category",
        },
        {
            name: fields.view,
            displayName: "Card Type",
            editorTemplate: "RadioButtonsField",
            defaultValue: "grid",
            horizontal: true,
            fieldType: "General",
            options: [
                {
                    displayName: "Grid",
                    value: "grid",
                },
                {
                    displayName: "Condensed",
                    value: "miniCard",
                },
                {
                    displayName: "Thumbnail",
                    value: "condensed",
                },
            ],
        },
        {
            name: fields.showImage,
            displayName: "Image",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            isEnabled: () => false,
            fieldType: "Contextual",
        },
        {
            name: fields.showTitle,
            displayName: "Title",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            isEnabled: () => false,
            fieldType: "Contextual",
        },
        {
            name: fields.showBrandName,
            displayName: "Brand Name",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            isVisible: (item: any) => item.fields[fields.view] !== "condensed",
        },
        {
            name: fields.showPartNumbers,
            displayName: "Part Numbers",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            isVisible: (item: any) => item.fields[fields.view] !== "condensed",
        },
        {
            name: fields.showPrice,
            displayName: "Price",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
        },
        {
            name: fields.showAddToCart,
            displayName: "Add to Cart",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
        },
        {
            name: fields.showAddToList,
            displayName: "Add to List",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
            isVisible: (item: any) => item.fields[fields.view] !== "condensed",
        },
        {
            name: fields.showAvailability,
            displayName: "Availability",
            editorTemplate: "CheckboxField",
            defaultValue: true,
            fieldType: "Contextual",
        },

        {
            name: fields.showImageBadges,
            displayName: "Image Badges",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
        {
            name: fields.maximumNumberOfImageBadges,
            editorTemplate: "DropDownField",
            isVisible: (widget: any) => widget.fields.showImageBadges,
            defaultValue: "4",
            options: [
                { displayName: "1", value: "1" },
                { displayName: "2", value: "2" },
                { displayName: "3", value: "3" },
                { displayName: "4", value: "4" },
                { displayName: "5", value: "5" },
                { displayName: "6", value: "6" },
                { displayName: "7", value: "7" },
                { displayName: "8", value: "8" },
            ],
            fieldType: "Contextual",
        },
        {
            name: fields.showTextBadges,
            displayName: "Text Badges",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "Contextual",
        },
        {
            name: fields.maximumNumberOfTextBadges,
            editorTemplate: "DropDownField",
            isVisible: (widget: any) => widget.fields.showTextBadges,
            defaultValue: "4",
            options: [
                { displayName: "1", value: "1" },
                { displayName: "2", value: "2" },
                { displayName: "3", value: "3" },
                { displayName: "4", value: "4" },
                { displayName: "5", value: "5" },
                { displayName: "6", value: "6" },
                { displayName: "7", value: "7" },
                { displayName: "8", value: "8" },
            ],
            fieldType: "Contextual",
        },
    ] as any,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CuratedProductCarousel),
    definition,
};

export default widgetModule;
